import React, { useContext, useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import InputAdornment from "@mui/material/InputAdornment";
import { CanvasContext, ICanvasComponent } from "../canvasContext.tsx";
import { GetTitlesCall } from "../../redux/api/title.api";

export type SlideOption = {
  label: string;
  tileUrl: string;
  thumbnailUrl: string;
};

function getTitleLabel(item: any, field = "title") {
  const list = item?.titleLocalizations?.filter((e: any) => e.field === field) || [];
  return list[0]?.value || item?.title || "";
}

function buildSlideEntries(titles: any[], sourceLabel: string): SlideOption[] {
  const entries: SlideOption[] = [];
  (titles || []).forEach((tile) => {
    const tileLabel = getTitleLabel(tile, "title");
    (tile.subtitles || []).forEach((sub: any) => {
      const slide = sub?.slides?.[0];
      if (!slide?.id) return;
      const slideLabel = getTitleLabel(sub, "title");
      const file = slide?.Files?.[0] || slide?.files?.[0];
      const baseUrl = file?.url;
      if (!baseUrl) return;
      const tileUrl = `${baseUrl}/{z}/{x}/{y}.png`;
      const thumbnailUrl = `${baseUrl}/3/3/3.png`;
      entries.push({
        label: `${sourceLabel}: ${tileLabel} / ${slideLabel}`,
        tileUrl,
        thumbnailUrl,
      });
    });
  });
  return entries;
}

const SlideElement = (props: ICanvasComponent) => {
  const { content, id, dimension, slideZoom = 0 } = props;
  const { actions } = useContext(CanvasContext);
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [slideOptions, setSlideOptions] = useState<SlideOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedOption, setSelectedOption] = useState<SlideOption | null>(null);

  useEffect(() => {
    if (slideOptions.length > 0) return;
    const lang = "en";
    setLoading(true);
    Promise.all([
      GetTitlesCall(lang, "his"),
      GetTitlesCall(lang, "emb"),
    ])
      .then(([hisRes, embRes]) => {
        const his = Array.isArray(hisRes) ? hisRes : [];
        const emb = Array.isArray(embRes) ? embRes : [];
        setSlideOptions([
          ...buildSlideEntries(his, "Histology"),
          ...buildSlideEntries(emb, "Embryology"),
        ]);
      })
      .catch(() => setSlideOptions([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!containerRef.current || !content) return;
    const tileUrl = typeof content === "string" ? content : (content as any)?.url;
    if (!tileUrl || !tileUrl.includes("{z}")) return;

    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    const initialZoom =
      typeof slideZoom === "number" && slideZoom >= 0
        ? Math.min(4, Math.max(0, slideZoom))
        : 0;
    const map = L.map(containerRef.current, {
      center: [0, 0],
      zoom: initialZoom,
      noWrap: true,
      maxBounds: [
        [-90, -180],
        [90, 180],
      ],
      maxBoundsViscosity: 1,
    });
    L.tileLayer(tileUrl, {
      maxZoom: 4,
      minZoom: 0,
      noWrap: true,
    }).addTo(map);

    const onZoomEnd = () => {
      const zoom = map.getZoom();
      actions?.updateCanvasData({ id, slideZoom: zoom });
      actions?.saveNow?.();
    };
    map.on("zoomend", onZoomEnd);
    mapRef.current = map;

    return () => {
      map.off("zoomend", onZoomEnd);
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [content, id]);

  useEffect(() => {
    const m = mapRef.current;
    if (!m || !containerRef.current) return;
    m.invalidateSize();
  }, [dimension?.width, dimension?.height]);

  const handleSelectSlide = (option: SlideOption | null) => {
    if (option) {
      actions?.updateCanvasData({ id, content: option.tileUrl });
      setSelectedOption(option);
      actions?.saveNow?.();
    }
  };

  const handleUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const url = e.target?.value?.trim();
    if (url) {
      actions?.updateCanvasData({ id, content: url });
      actions?.saveNow?.();
    }
  };

  if (!content) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          background: "#e8e8e8",
          padding: 8,
          overflow: "auto",
        }}
      >
        <span style={{ fontSize: 12, marginBottom: 6, display: "block" }}>
          Search and select a slide
        </span>
        <Autocomplete
          size="small"
          fullWidth
          loading={loading}
          options={slideOptions}
          value={selectedOption}
          onChange={(_, value) => handleSelectSlide(value)}
          getOptionLabel={(opt) => opt?.label ?? ""}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search by name…"
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <InputAdornment position="start">🔍</InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
              }}
            />
          )}
          renderOption={(props, opt) => (
            <li {...props} key={`${opt.label}-${opt.tileUrl}`}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  component="img"
                  src={opt.thumbnailUrl}
                  alt=""
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = "none";
                  }}
                  sx={{
                    width: 40,
                    height: 40,
                    objectFit: "cover",
                    borderRadius: 1,
                    flexShrink: 0,
                    bgcolor: "grey.200",
                  }}
                />
                <span style={{ fontSize: 13 }}>{opt.label}</span>
              </Box>
            </li>
          )}
        />
        <span style={{ fontSize: 11, marginTop: 8, marginBottom: 4 }}>Or paste tile URL</span>
        <input
          type="url"
          placeholder=".../{z}/{x}/{y}.png"
          style={{ width: "100%", padding: 6, fontSize: 12, boxSizing: "border-box" }}
          onBlur={handleUrlBlur}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        minHeight: 80,
      }}
    >
      <div
        ref={containerRef}
        style={{
          width: "100%",
          height: "100%",
          minHeight: 80,
        }}
      />
    </div>
  );
};

export default SlideElement;
